import { useEffect, type ReactNode } from 'react';
import clsx from 'clsx';
import { Box } from '@customerio/pluma-components/react';
import { darkTheme, newTheme } from '@customerio/pluma-components/css';
import type { SignupColorScheme } from '../utils/signup-color-scheme';
import { SignupHeader } from './signup-header';
import * as styles from '../index.css';

/**
 * Forced-dark also themes the portals (the error snackbar) by putting the
 * Pluma dark theme class on `<body>`; the page wrapper carries it too so the
 * funnel itself never depends on the body class. Cleaned up on unmount — the
 * funnel exits by full-page redirect, but the flag can flip mid-session in dev.
 */
function useBodyDarkTheme(scheme: SignupColorScheme) {
  useEffect(() => {
    // Removes only what it added: another owner (Pluma's theme manager) may
    // already hold the class, and the funnel exits by SPA navigation — so an
    // unconditional cleanup would strip it for the rest of the session.
    if (scheme !== 'dark' || document.body.classList.contains(darkTheme.className)) {
      return;
    }
    document.body.classList.add(darkTheme.className);
    return () => {
      document.body.classList.remove(darkTheme.className);
    };
  }, [scheme]);
}

const WRAPPER_THEME_CLASS = {
  // `newTheme` is this app's ambient light theme, so forced-light matches what
  // an OS-light visitor already sees.
  light: newTheme.className,
  dark: darkTheme.className,
  default: false,
} satisfies Record<SignupColorScheme, string | false>;

type SignupShellProps = {
  scheme: SignupColorScheme;
  /** The startup application's entry step swaps the standard surface for its own. */
  isStartupFormStep: boolean;
  showLoginLink: boolean;
  onLoginLinkClick?: () => void;
  children: ReactNode;
};

/** The funnel's themed page wrapper and header, shared by the steps and the post-register hold. */
export function SignupShell({
  scheme,
  isStartupFormStep,
  showLoginLink,
  onLoginLinkClick,
  children,
}: SignupShellProps) {
  const isDark = scheme === 'dark';
  useBodyDarkTheme(scheme);
  const surfaceClass = isStartupFormStep ? styles.startupSurface : styles.signupSurface;

  return (
    <Box
      className={clsx(WRAPPER_THEME_CLASS[scheme], isDark ? styles.darkWrapper : surfaceClass)}
      style={{ minHeight: '100vh' }}
    >
      <SignupHeader showLoginLink={showLoginLink} onLoginLinkClick={onLoginLinkClick} isDark={isDark} />
      {children}
    </Box>
  );
}
